// src/components/search/AddressResultsList.tsx
import {
  Box,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import type { AddressResult, NotePreview } from "@/search/types";

interface AddressResultsListProps {
  results: AddressResult[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  loadMoreRef: React.RefObject<HTMLDivElement | null>;
  onSelectAddress: (address: AddressResult) => void;
}

export default function AddressResultsList({
  results,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  loadMoreRef,
  onSelectAddress,
}: AddressResultsListProps) {
  // Show loading skeleton
  if (isLoading && results.length === 0) {
    return (
      <Box sx={{ px: 2 }}>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="rounded" height={64} />
          </Box>
        ))}
      </Box>
    );
  }

  if (results.length === 0) {
    return null;
  }

  return (
    <>
      <List disablePadding>
        {results.map((addr, idx) => (
          <AddressResultItem
            key={addr.id}
            address={addr}
            isLast={idx === results.length - 1}
            onSelect={() => onSelectAddress(addr)}
          />
        ))}
      </List>

      {hasNextPage && <Box ref={loadMoreRef} sx={{ height: 1 }} />}

      {isFetchingNextPage && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </>
  );
}

interface AddressResultItemProps {
  address: AddressResult;
  isLast: boolean;
  onSelect: () => void;
}

// const flashFade = keyframes`
//   0% { background-color: rgba(255, 248, 209, 0.7); }
//   100% { background-color: white; }
// `;

const flashFade = keyframes`
  0% { background-color: #fff3b0; }
  30% { background-color: #fff8d1; }
  100% { background-color: white; }
`;

function AddressResultItem({ address, isLast, onSelect }: AddressResultItemProps) {
  const note = address.latest_note;
  const flashTag = address.flashTag;

  return (
    <Box>
      <ListItemButton
        onClick={onSelect}
        sx={{
          backgroundColor: "white",
          px: 2,
          py: 1.2,
          animation: flashTag
            ? `${flashFade} 1.2s ease-out forwards`
            : undefined,
          willChange: flashTag ? "background-color" : undefined,
        }}
      >
        <ListItemText
          primary={
            <Typography variant="body1" fontWeight={600}>
              {address.to_display}
            </Typography>
          }
          slotProps={{ secondary: { component: "div" } }}
          secondary={<AddressSecondaryContent customer={address.customer} note={note} />}
        />
        <ArrowForwardIosIcon sx={{ fontSize: 16, opacity: 0.35 }} />
      </ListItemButton>

      {!isLast && <Divider sx={{ ml: 2 }} />}
    </Box>
  );
}

interface AddressSecondaryContentProps {
  customer?: AddressResult["customer"];
  note?: NotePreview | null;
}

function AddressSecondaryContent({ customer, note }: AddressSecondaryContentProps) {
  return (
    <Stack spacing={0.3} sx={{ mt: 0.3 }}>
      {/* Customer name */}
      {customer?.name && (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {customer.name}
        </Typography>
      )}

      {/* Status + note */}
      {note && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.3, mt: customer ? 0 : 0.2 }}
        >
          <strong>{note.status_display}</strong>
          {note.note ? ` — ${note.note}` : ""}
        </Typography>
      )}

      {/* Compact REGULAR / QUOTED metadata */}
      {note && (note.status === "REGULAR" || note.status === "QUOTED") && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {[
            note.frequency_display && `Freq: ${note.frequency_display}`,
            note.payment_type_display && `Pay: ${note.payment_type_display}`,
            note.price !== null && `£${note.price}`,
            note.notification_type_display && `Notify: ${note.notification_type_display}`,
            note.followup_date &&
              `Follow-up: ${new Date(note.followup_date).toLocaleDateString()}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
      )}

      {/* Username + timestamp */}
      {note && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1.2, mt: 0.2 }}
        >
          {note.username ? `${note.username} — ` : ""}
          {new Date(note.created_at).toLocaleString()}
        </Typography>
      )}
    </Stack>
  );
}
